import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Routes, Route } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { buildLocationTelemetry } from '@/lib/location';
import Sidebar from '@/components/layout/Sidebar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import DashboardDateTimeWidget from '@/components/layout/DashboardDateTimeWidget';
import { toast } from 'sonner';
import { Truck, Package, MapPin, Clock, CheckCircle, Coffee, Power, Gauge, Timer, Route as RouteIcon, Navigation, ShieldCheck, Upload, AlertTriangle } from 'lucide-react';
import mapboxgl from "mapbox-gl";
import MapView, { Layer, Marker, NavigationControl, Source } from "react-map-gl/mapbox";
import "mapbox-gl/dist/mapbox-gl.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAP_STYLE = "mapbox://styles/mapbox/navigation-night-v1";
const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;

const statusColors = {
  pending: 'bg-amber-950/50 text-amber-400 border-amber-900/50',
  assigned: 'bg-zinc-800 text-zinc-400 border-zinc-700',
  in_transit: 'bg-emerald-950/50 text-emerald-400 border-emerald-900/50',
  delivered: 'bg-emerald-950/50 text-emerald-400 border-emerald-900/50',
  cancelled: 'bg-red-950/50 text-red-400 border-red-900/50',
};

const driverStatusConfig = {
  available: { label: 'Available', icon: CheckCircle, color: 'text-emerald-400', bg: 'bg-emerald-950/50' },
  on_duty: { label: 'On Duty', icon: Truck, color: 'text-primary', bg: 'bg-primary/10' },
  off_duty: { label: 'Off Duty', icon: Power, color: 'text-zinc-400', bg: 'bg-zinc-800' },
  on_break: { label: 'On Break', icon: Coffee, color: 'text-amber-400', bg: 'bg-amber-950/50' },
};

const driverMapMarkerColor = {
  available: '#22c55e',
  on_duty: '#3b82f6',
  on_break: '#f59e0b',
  off_duty: '#94a3b8',
};

const driverRouteGlowLayer = {
  id: "driver-live-route-glow",
  type: "line",
  paint: {
    "line-color": "#60a5fa",
    "line-width": ["interpolate", ["linear"], ["zoom"], 4, 5, 8, 8, 12, 12],
    "line-opacity": 0.26,
    "line-blur": 1.1,
  },
};

const driverRouteLineLayer = {
  id: "driver-live-route-line",
  type: "line",
  paint: {
    "line-color": "#f97316",
    "line-width": ["interpolate", ["linear"], ["zoom"], 4, 2.8, 8, 4.5, 12, 6.2],
    "line-opacity": 0.96,
  },
};

const TRACKING_STORAGE_VERSION = 1;
const getTrackingStorageKey = (userId) => `eagle_driver_tracking_${userId}`;
const getDriverStatusKey = (userId) => `eagle_driver_last_status_${userId}`;
const isValidCoordinatePair = (latitude, longitude) => Number.isFinite(Number(latitude)) && Number.isFinite(Number(longitude));
const sanitizeRouteCoordinates = (coordinates) =>
  (Array.isArray(coordinates) ? coordinates : [])
    .map((pair) => [Number(pair?.[0]), Number(pair?.[1])])
    .filter(([lng, lat]) => Number.isFinite(lng) && Number.isFinite(lat));

const buildDriverRouteCoordinates = ({ currentPosition, routeGeometry, destination }) => {
  const routeCoordinates = sanitizeRouteCoordinates(routeGeometry?.coordinates);
  const start = isValidCoordinatePair(currentPosition?.latitude, currentPosition?.longitude)
    ? [Number(currentPosition.longitude), Number(currentPosition.latitude)]
    : null;
  const end = isValidCoordinatePair(destination?.latitude, destination?.longitude)
    ? [Number(destination.longitude), Number(destination.latitude)]
    : null;

  if (!routeCoordinates.length) {
    return start && end ? [start, end] : [];
  }

  const normalized = [...routeCoordinates];
  if (start) {
    const [firstLng, firstLat] = normalized[0];
    if (Math.abs(firstLng - start[0]) > 0.0001 || Math.abs(firstLat - start[1]) > 0.0001) {
      normalized.unshift(start);
    } else {
      normalized[0] = start;
    }
  }

  if (end) {
    const [lastLng, lastLat] = normalized[normalized.length - 1];
    if (Math.abs(lastLng - end[0]) > 0.0001 || Math.abs(lastLat - end[1]) > 0.0001) {
      normalized.push(end);
    } else {
      normalized[normalized.length - 1] = end;
    }
  }

  return normalized;
};

const formatLiveEta = (seconds) => {
  const total = Math.max(0, Math.floor(Number(seconds || 0)));
  if (!total) return "-";
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  return hours > 0 ? `${hours}h ${minutes}m` : `${Math.max(1, minutes)}m`;
};

const formatLiveDistance = (miles) => {
  const value = Number(miles);
  if (!Number.isFinite(value)) return "-";
  if (value < 0.1) return "< 0.1 mi";
  return `${value.toFixed(value >= 10 ? 0 : 1)} mi`;
};

const DriverHome = () => {
  const { user } = useAuth();
  const [shipments, setShipments] = useState([]);
  const [currentStatus, setCurrentStatus] = useState(user?.driver_status || 'off_duty');
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isTracking, setIsTracking] = useState(false);
  const [trackingPermission, setTrackingPermission] = useState('unknown');
  const [liveSpeedMph, setLiveSpeedMph] = useState(0);
  const [driveTimeSec, setDriveTimeSec] = useState(0);
  const [milesDriven, setMilesDriven] = useState(0);
  const [lastSeenAt, setLastSeenAt] = useState(null);
  const [sessionStartedAt, setSessionStartedAt] = useState(null);
  const [currentPosition, setCurrentPosition] = useState(null);
  const [liveTripLocation, setLiveTripLocation] = useState(null);
  const [driverViewState, setDriverViewState] = useState({ latitude: 39.8283, longitude: -98.5795, zoom: 3.5 });
  const [kycInfo, setKycInfo] = useState({ status: 'not_submitted', submissions: [] });
  const [driveHistory, setDriveHistory] = useState({ summary: {}, sessions: [] });
  const [kycDocType, setKycDocType] = useState('cdl_front');
  const [kycFile, setKycFile] = useState(null);
  const [kycUploading, setKycUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const watchIdRef = useRef(null);
  const lastCoordRef = useRef(null);
  const lastKnownCoordsRef = useRef(null);
  const driveTimeRef = useRef(0);
  const milesRef = useRef(0);
  const lastUpdateSentAtRef = useRef(0);
  const driveTimerRef = useRef(null);
  const autoPromptedRef = useRef(false);
  const statusRef = useRef(currentStatus);

  const applyLiveDriverSnapshot = useCallback((payload) => {
    if (!payload || typeof payload !== "object") return;
    setLiveTripLocation(payload);
    const latitude = Number(payload.latitude ?? payload.lat);
    const longitude = Number(payload.longitude ?? payload.lng);
    if (Number.isFinite(latitude) && Number.isFinite(longitude)) {
      setCurrentPosition((prev) => {
        if (prev && Math.abs(prev.latitude - latitude) < 0.000001 && Math.abs(prev.longitude - longitude) < 0.000001) {
          return prev;
        }
        return { latitude, longitude };
      });
      lastKnownCoordsRef.current = {
        ...(lastKnownCoordsRef.current || {}),
        latitude,
        longitude,
        accuracy: payload.accuracy_meters ?? lastKnownCoordsRef.current?.accuracy,
        heading: payload.heading ?? lastKnownCoordsRef.current?.heading,
        altitude: payload.altitude ?? lastKnownCoordsRef.current?.altitude,
      };
      setDriverViewState((prev) => ({
        ...prev,
        latitude,
        longitude,
        zoom: Math.max(prev.zoom, payload.trip_status === "active" ? 12 : 11),
      }));
    }
  }, []);

  const refreshDriverLiveMap = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      if (response.data?.driver_status && driverStatusConfig[response.data.driver_status]) {
        setCurrentStatus(response.data.driver_status);
      }
      if (response.data?.current_location) {
        applyLiveDriverSnapshot(response.data.current_location);
      }
    } catch (_) {
      // Keep live map refresh non-blocking.
    }
  }, [applyLiveDriverSnapshot]);

  useEffect(() => {
    statusRef.current = currentStatus;
  }, [currentStatus]);

  useEffect(() => {
    fetchShipments();
    fetchKycInfo();
    fetchDriveHistory();
    refreshDriverLiveMap();
  }, [refreshDriverLiveMap]);

  const fetchKycInfo = async () => {
    try {
      const response = await axios.get(`${API}/kyc/me`);
      setKycInfo(response.data || { status: 'not_submitted', submissions: [] });
    } catch (error) {
      // keep non-blocking
    }
  };

  const fetchDriveHistory = async () => {
    try {
      const response = await axios.get(`${API}/driver/drive-time/history?limit=25`);
      const payload = response.data || { summary: {}, sessions: [] };
      setDriveHistory(payload);
      if (typeof payload.summary?.total_drive_time_seconds === 'number') {
        const seconds = Math.max(0, Math.floor(payload.summary.total_drive_time_seconds));
        setDriveTimeSec(seconds);
        driveTimeRef.current = seconds;
      }
    } catch (error) {
      // Keep drive time history non-blocking in UI.
    }
  };

  useEffect(() => {
    return () => {
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
      }
      if (driveTimerRef.current) {
        clearInterval(driveTimerRef.current);
      }
    };
  }, []);

  const fetchShipments = async () => {
    try {
      const response = await axios.get(`${API}/shipments`);
      setShipments(response.data);
    } catch (error) {
      toast.error('Failed to fetch loads');
    } finally {
      setIsLoading(false);
    }
  };

  const toLocationPayload = useCallback((coordsLike, speedMph) => buildLocationTelemetry({
    latitude: coordsLike.latitude,
    longitude: coordsLike.longitude,
    lat: coordsLike.latitude,
    lng: coordsLike.longitude,
    accuracy_meters: coordsLike.accuracy,
    speed_mph: speedMph,
    heading: coordsLike.heading,
    altitude: coordsLike.altitude,
    tracked_at: new Date().toISOString(),
    miles_driven_session: Number(milesRef.current.toFixed(2)),
    drive_time_seconds: driveTimeRef.current,
  }), []);

  const updateDriverStatus = async (status) => {
    setIsUpdating(true);
    try {
      const basePayload = { status };
      const known = lastKnownCoordsRef.current;
      const speed = Number(liveSpeedMph.toFixed(1));
      if (known?.latitude && known?.longitude) {
        basePayload.location = await toLocationPayload(known, speed);
      }
      const response = await axios.put(`${API}/users/driver-status`, basePayload);
      setCurrentStatus(status);
      if (response.data?.current_location) {
        applyLiveDriverSnapshot(response.data.current_location);
      }
      if (user?.id) {
        localStorage.setItem(getDriverStatusKey(user.id), status);
      }
      await fetchDriveHistory();
      toast.success(`Status updated to ${driverStatusConfig[status].label}`);
    } catch (error) {
      toast.error('Failed to update status');
    } finally {
      setIsUpdating(false);
    }
  };

  const haversineMiles = useCallback((a, b) => {
    const toRad = (deg) => (deg * Math.PI) / 180;
    const earthRadiusMiles = 3958.8;
    const dLat = toRad(b.latitude - a.latitude);
    const dLng = toRad(b.longitude - a.longitude);
    const lat1 = toRad(a.latitude);
    const lat2 = toRad(b.latitude);
    const h =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
    return earthRadiusMiles * c;
  }, []);

  const sendLocationUpdate = useCallback(async (coords, speedMph) => {
    const now = Date.now();
    if (now - lastUpdateSentAtRef.current < 2000) return;
    lastUpdateSentAtRef.current = now;
    try {
      const locationPayload = await toLocationPayload(coords, speedMph);
      const response = await axios.put(`${API}/users/driver-status`, {
        status: statusRef.current,
        location: locationPayload,
      });
      if (response.data?.current_location) {
        applyLiveDriverSnapshot(response.data.current_location);
      }
    } catch (error) {
      // Non-blocking; tracking should continue even if one update fails.
    }
  }, [applyLiveDriverSnapshot, toLocationPayload]);

  const stopTracking = useCallback(() => {
    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
    if (driveTimerRef.current) {
      clearInterval(driveTimerRef.current);
      driveTimerRef.current = null;
    }
    setIsTracking(false);
    setLiveSpeedMph(0);
  }, []);

  const startTracking = useCallback(async ({ resetSession = true } = {}) => {
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported on this device');
      return;
    }
    if (watchIdRef.current !== null) return;

    if (resetSession) {
      lastCoordRef.current = null;
      driveTimeRef.current = 0;
      milesRef.current = 0;
      setDriveTimeSec(0);
      setMilesDriven(0);
      setLiveSpeedMph(0);
      setSessionStartedAt(new Date().toISOString());
    }

    try {
      if (navigator.permissions && navigator.permissions.query) {
        const permissionResult = await navigator.permissions.query({ name: 'geolocation' });
        setTrackingPermission(permissionResult.state);
      }
    } catch (error) {
      // Some browsers do not support permissions API for geolocation.
    }

    if (driveTimerRef.current) {
      clearInterval(driveTimerRef.current);
    }
    driveTimerRef.current = setInterval(() => {
      if (statusRef.current !== 'on_duty') return;
      setDriveTimeSec((prev) => {
        const next = prev + 1;
        driveTimeRef.current = next;
        return next;
      });
    }, 1000);

    const watchId = navigator.geolocation.watchPosition(
      async (position) => {
        setTrackingPermission('granted');
        setIsTracking(true);
        setLastSeenAt(new Date());
        const coords = position.coords;
        lastKnownCoordsRef.current = {
          latitude: coords.latitude,
          longitude: coords.longitude,
          accuracy: coords.accuracy,
          heading: coords.heading,
          altitude: coords.altitude,
        };
        setCurrentPosition({ latitude: coords.latitude, longitude: coords.longitude });
        setDriverViewState((prev) => ({
          ...prev,
          latitude: coords.latitude,
          longitude: coords.longitude,
          zoom: Math.max(prev.zoom, 11),
        }));
        const point = {
          latitude: coords.latitude,
          longitude: coords.longitude,
        };

        let speedMph = 0;
        if (typeof coords.speed === 'number' && !Number.isNaN(coords.speed) && coords.speed >= 0) {
          speedMph = coords.speed * 2.23694;
        } else if (lastCoordRef.current) {
          const segmentMiles = haversineMiles(lastCoordRef.current, point);
          const elapsedSec = (position.timestamp - (lastCoordRef.current.timestamp || position.timestamp)) / 1000;
          if (elapsedSec > 0) {
            speedMph = (segmentMiles / elapsedSec) * 3600;
          }
        }
        setLiveSpeedMph(speedMph);

        if (statusRef.current === 'on_duty' && lastCoordRef.current) {
          const segmentMiles = haversineMiles(lastCoordRef.current, point);
          if (segmentMiles > 0.001) {
            milesRef.current += segmentMiles;
            setMilesDriven(Number(milesRef.current.toFixed(2)));
          }
        }

        lastCoordRef.current = {
          ...point,
          timestamp: position.timestamp,
        };

        await sendLocationUpdate(coords, Number(speedMph.toFixed(1)));
      },
      (error) => {
        setTrackingPermission('denied');
        stopTracking();
        toast.error(error.message || 'Location permission denied');
      },
      {
        enableHighAccuracy: true,
        maximumAge: 1000,
        timeout: 10000,
      },
    );

    watchIdRef.current = watchId;
  }, [haversineMiles, sendLocationUpdate, stopTracking]);

  const formatDriveTime = (totalSeconds) => {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  };

  useEffect(() => {
    if (autoPromptedRef.current || isTracking) return;
    autoPromptedRef.current = true;
    startTracking({ resetSession: false });
  }, [isTracking, startTracking]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      fetchDriveHistory();
    }, 30000);
    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    const intervalId = setInterval(() => {
      refreshDriverLiveMap();
    }, 10000);
    return () => clearInterval(intervalId);
  }, [refreshDriverLiveMap]);

  useEffect(() => {
    if (!user?.id) return;
    try {
      const raw = localStorage.getItem(getTrackingStorageKey(user.id));
      if (!raw) return;
      const saved = JSON.parse(raw);
      if (saved?.version !== TRACKING_STORAGE_VERSION) return;

      if (saved.currentStatus && driverStatusConfig[saved.currentStatus]) {
        setCurrentStatus(saved.currentStatus);
      }
      const statusFromKey = localStorage.getItem(getDriverStatusKey(user.id));
      if (statusFromKey && driverStatusConfig[statusFromKey]) {
        setCurrentStatus(statusFromKey);
      }
      if (saved.trackingPermission) setTrackingPermission(saved.trackingPermission);
      if (typeof saved.driveTimeSec === 'number') {
        const driveSec = Math.max(0, Math.floor(saved.driveTimeSec));
        setDriveTimeSec(driveSec);
        driveTimeRef.current = driveSec;
      }
      if (typeof saved.milesDriven === 'number') {
        const miles = Math.max(0, saved.milesDriven);
        setMilesDriven(miles);
        milesRef.current = miles;
      }
      if (typeof saved.liveSpeedMph === 'number') setLiveSpeedMph(saved.liveSpeedMph);
      if (saved.lastSeenAt) setLastSeenAt(new Date(saved.lastSeenAt));
      if (saved.sessionStartedAt) setSessionStartedAt(saved.sessionStartedAt);
      if (saved.lastCoord) lastCoordRef.current = saved.lastCoord;
      if (saved.lastKnownCoords) {
        lastKnownCoordsRef.current = saved.lastKnownCoords;
        setCurrentPosition({ latitude: saved.lastKnownCoords.latitude, longitude: saved.lastKnownCoords.longitude });
        setDriverViewState((prev) => ({
          ...prev,
          latitude: saved.lastKnownCoords.latitude,
          longitude: saved.lastKnownCoords.longitude,
          zoom: Math.max(prev.zoom, 11),
        }));
      }

      if (saved.isTracking) {
        setIsTracking(true);
        autoPromptedRef.current = true;
        startTracking({ resetSession: false });
      }
    } catch (error) {
      // Ignore corrupted local state.
    }
  }, [user?.id, startTracking]);

  useEffect(() => {
    if (!user?.id) return;
    const payload = {
      version: TRACKING_STORAGE_VERSION,
      currentStatus,
      isTracking,
      trackingPermission,
      liveSpeedMph,
      driveTimeSec,
      milesDriven,
      lastSeenAt: lastSeenAt ? new Date(lastSeenAt).toISOString() : null,
      sessionStartedAt,
      lastCoord: lastCoordRef.current,
      lastKnownCoords: lastKnownCoordsRef.current,
    };
    localStorage.setItem(getTrackingStorageKey(user.id), JSON.stringify(payload));
  }, [user?.id, currentStatus, isTracking, trackingPermission, liveSpeedMph, driveTimeSec, milesDriven, lastSeenAt, sessionStartedAt]);

  const updateShipmentStatus = async (shipmentId, status) => {
    try {
      await axios.put(`${API}/shipments/${shipmentId}/status`, { status });
      toast.success('Shipment status updated');
      fetchShipments();
    } catch (error) {
      toast.error('Failed to update shipment');
    }
  };

  const activeLoad = shipments.find(s => s.status === 'in_transit' || s.status === 'assigned');
  const pendingLoads = shipments.filter(s => s.status === 'assigned');
  const completedLoads = shipments.filter(s => s.status === 'delivered');

  const statusConfig = driverStatusConfig[currentStatus];
  const latestKyc = kycInfo.submissions?.[0];
  const driveSummary = driveHistory.summary || {};
  const recentDriveSessions = driveHistory.sessions || [];
  const liveDestination = useMemo(() => {
    if (!isValidCoordinatePair(liveTripLocation?.destination_lat, liveTripLocation?.destination_lng)) return null;
    return {
      latitude: Number(liveTripLocation.destination_lat),
      longitude: Number(liveTripLocation.destination_lng),
      name: liveTripLocation.destination_name || activeLoad?.destination || "Destination",
    };
  }, [activeLoad?.destination, liveTripLocation]);
  const liveRouteCoordinates = useMemo(
    () => buildDriverRouteCoordinates({ currentPosition, routeGeometry: liveTripLocation?.route_geometry, destination: liveDestination }),
    [currentPosition, liveDestination, liveTripLocation],
  );
  const liveRouteGeojson = useMemo(
    () => ({
      type: "FeatureCollection",
      features: liveRouteCoordinates.length >= 2
        ? [
            {
              type: "Feature",
              properties: {},
              geometry: {
                type: "LineString",
                coordinates: liveRouteCoordinates,
              },
            },
          ]
        : [],
    }),
    [liveRouteCoordinates],
  );
  const liveTripStatus = String(liveTripLocation?.trip_status || activeLoad?.status || "idle").replaceAll("_", " ");
  const liveNextTurn = liveTripLocation?.next_turn_instruction || "";
  const requiredDocSteps = [
    { key: 'identity_info', label: 'Identity Info', tip: 'Confirm your legal name, phone, and account details.' },
    { key: 'cdl_front', label: 'Government ID Front', tip: 'Upload a clear front image of CDL/government ID.' },
    { key: 'selfie_verification', label: 'Selfie Verification', tip: 'Use front camera for a clear selfie matching your ID.' },
    { key: 'proof_of_address', label: 'Proof of Address', tip: 'Utility bill or official letter with your current address.' },
    { key: 'review_submit', label: 'Review & Submit', tip: 'Check all uploads and submit for compliance review.' },
  ];
  const submittedDocTypes = new Set((kycInfo.submissions || []).map((s) => s.doc_type));
  const wizardProgress = Math.min(
    100,
    Math.round(((submittedDocTypes.has('cdl_front') ? 1 : 0) + (submittedDocTypes.has('selfie_verification') ? 1 : 0) + (submittedDocTypes.has('proof_of_address') ? 1 : 0) + (kycInfo.status === 'pending' || kycInfo.status === 'verified' ? 1 : 0)) / 4 * 100),
  );

  const uploadKycDocument = async () => {
    if (!kycFile) {
      toast.error('Choose a document first');
      return;
    }
    setKycUploading(true);
    try {
      const form = new FormData();
      form.append('doc_type', kycDocType);
      form.append('file', kycFile);
      await axios.post(`${API}/kyc/driver/upload`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('KYC document uploaded and sent for review');
      setKycFile(null);
      await fetchKycInfo();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload document');
    } finally {
      setKycUploading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Status Card */}
      <Card className="card-hover overflow-hidden">
        <div className={`h-2 ${currentStatus === 'available' ? 'bg-emerald-500' : currentStatus === 'on_duty' ? 'bg-primary' : 'bg-zinc-600'}`} />
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <div className={`w-14 h-14 rounded-full flex items-center justify-center ${statusConfig.bg}`}>
                <statusConfig.icon className={`w-7 h-7 ${statusConfig.color}`} />
              </div>
              <div>
                <p className="text-sm text-muted-foreground uppercase tracking-wider">Current Status</p>
                <p className={`text-2xl font-heading font-bold ${statusConfig.color}`}>{statusConfig.label}</p>
              </div>
            </div>
          </div>
          
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">Update Status</p>
            <div className="grid grid-cols-2 gap-3">
              {Object.entries(driverStatusConfig).map(([key, config]) => (
                <Button
                  key={key}
                  variant={currentStatus === key ? 'default' : 'outline'}
                  className={currentStatus === key ? 'bg-primary' : ''}
                  onClick={() => updateDriverStatus(key)}
                  disabled={isUpdating}
                  data-testid={`status-${key}-btn`}
                >
                  <config.icon className="w-4 h-4 mr-2" />
                  {config.label}
                </Button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Live Tracking Card */}
      <Card className="card-hover overflow-hidden">
        <div className={`h-1.5 ${isTracking ? 'bg-emerald-500' : 'bg-zinc-700'}`} />
        <CardHeader>
          <CardTitle className="font-heading text-xl flex items-center gap-2">
            <Navigation className="w-5 h-5 text-primary" />
            Live Drive Tracker
          </CardTitle>
          <CardDescription>
            Track movement in real time with speed, drive time, and miles. Data syncs to dispatch/admin map.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="grid grid-cols-3 gap-3">
            <div className="rounded-lg border border-border p-4 bg-muted/30">
              <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-wider mb-2">
                <Gauge className="w-4 h-4" />
                Speed
              </div>
              <p className="text-2xl font-heading font-bold">{liveSpeedMph.toFixed(1)} mph</p>
            </div>
            <div className="rounded-lg border border-border p-4 bg-muted/30">
              <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-wider mb-2">
                <Timer className="w-4 h-4" />
                Drive Time
              </div>
              <p className="text-2xl font-heading font-bold">{formatDriveTime(driveTimeSec)}</p>
            </div>
            <div className="rounded-lg border border-border p-4 bg-muted/30">
              <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-wider mb-2">
                <RouteIcon className="w-4 h-4" />
                Miles
              </div>
              <p className="text-2xl font-heading font-bold">{milesDriven.toFixed(2)} mi</p>
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="text-sm text-muted-foreground">
              Permission: <span className="font-semibold text-foreground capitalize">{trackingPermission}</span>
              {lastSeenAt && <span className="ml-3">Last update: {lastSeenAt.toLocaleTimeString()}</span>}
              {sessionStartedAt && <span className="ml-3">Session started: {new Date(sessionStartedAt).toLocaleTimeString()}</span>}
            </div>
            <div className="flex gap-2">
              {!isTracking ? (
                <Button className="bg-primary hover:bg-primary/90" onClick={startTracking} data-testid="start-tracking-btn">
                  Start Tracking
                </Button>
              ) : (
                <Button variant="outline" onClick={stopTracking} data-testid="stop-tracking-btn">
                  Stop Tracking
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Drive Time History */}
      <Card className="card-hover overflow-hidden">
        <CardHeader>
          <CardTitle className="font-heading text-xl flex items-center gap-2">
            <Clock className="w-5 h-5 text-primary" />
            On-Duty Time History
          </CardTitle>
          <CardDescription>
            Drive time runs only while On Duty and pauses when Available, On Break, or Off Duty.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-lg border border-border p-4 bg-muted/30">
              <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Total On-Duty</p>
              <p className="text-xl font-heading font-bold">{formatDriveTime(driveSummary.total_drive_time_seconds || 0)}</p>
            </div>
            <div className="rounded-lg border border-border p-4 bg-muted/30">
              <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Today</p>
              <p className="text-xl font-heading font-bold">{formatDriveTime(driveSummary.today_drive_time_seconds || 0)}</p>
            </div>
          </div>
          <div className="rounded-lg border border-border overflow-hidden">
            <div className="p-3 border-b border-border/60 text-xs uppercase tracking-wider text-muted-foreground">Recent Sessions</div>
            <div className="max-h-64 overflow-y-auto">
              {recentDriveSessions.length === 0 ? (
                <p className="p-3 text-sm text-muted-foreground">No completed on-duty sessions yet.</p>
              ) : (
                recentDriveSessions.map((session) => (
                  <div key={session.id} className="p-3 border-b border-border/40 text-sm">
                    <p className="font-semibold">{formatDriveTime(session.duration_seconds || 0)}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {session.session_started_at ? new Date(session.session_started_at).toLocaleString() : '-'} to {session.session_ended_at ? new Date(session.session_ended_at).toLocaleString() : '-'}
                    </p>
                    <p className="text-xs text-muted-foreground capitalize mt-1">
                      Ended on: {String(session.stopped_by_status || '-').replaceAll('_', ' ')}
                    </p>
                  </div>
                ))
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Driver Live Map */}
      <Card className="card-hover overflow-hidden">
        <CardHeader>
          <CardTitle className="font-heading text-xl flex items-center gap-2">
            <MapPin className="w-5 h-5 text-primary" />
            Live Driver Map
          </CardTitle>
          <CardDescription>
            Real-time device location synced with Admin Operations Map and driver status state.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="relative rounded-xl overflow-hidden border border-border">
            <MapView
              mapboxAccessToken={MAPBOX_TOKEN}
              mapLib={mapboxgl}
              mapStyle={MAP_STYLE}
              latitude={driverViewState.latitude}
              longitude={driverViewState.longitude}
              zoom={driverViewState.zoom}
              onMove={(evt) => setDriverViewState(evt.viewState)}
              style={{ width: "100%", height: "min(46vh, 420px)" }}
            >
              <NavigationControl position="top-left" />
              {liveRouteGeojson.features.length > 0 && (
                <Source id="driver-live-route" type="geojson" data={liveRouteGeojson}>
                  <Layer {...driverRouteGlowLayer} />
                  <Layer {...driverRouteLineLayer} />
                </Source>
              )}
              {currentPosition && (
                <Marker longitude={currentPosition.longitude} latitude={currentPosition.latitude} anchor="bottom">
                  <div
                    className="w-4 h-4 rounded-full border-2 border-white"
                    style={{
                      backgroundColor: driverMapMarkerColor[currentStatus] || '#94a3b8',
                      boxShadow: `0 0 14px ${driverMapMarkerColor[currentStatus] || '#94a3b8'}`,
                    }}
                  />
                </Marker>
              )}
              {liveDestination && (
                <Marker longitude={liveDestination.longitude} latitude={liveDestination.latitude} anchor="bottom">
                  <div className="flex h-4 w-4 items-center justify-center rounded-full border-2 border-white bg-orange-500 shadow-[0_0_18px_rgba(249,115,22,0.65)]" />
                </Marker>
              )}
            </MapView>
            <div className="absolute left-3 top-3 z-10 max-w-[min(70%,28rem)] rounded-lg border border-stone-700/70 bg-black/72 px-3 py-2 text-xs text-stone-100 backdrop-blur">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="outline" className="border-orange-500/40 bg-orange-500/10 text-orange-100">
                  {liveTripStatus}
                </Badge>
                <span>ETA {formatLiveEta(liveTripLocation?.eta_seconds)}</span>
                <span>{formatLiveDistance(liveTripLocation?.distance_remaining_miles)}</span>
              </div>
              <p className="mt-2 font-medium text-stone-50">
                {liveDestination?.name || activeLoad?.destination || "No active destination"}
              </p>
              <p className="mt-1 text-stone-300">
                {liveNextTurn || (liveRouteGeojson.features.length > 0 ? "Live route is tracking your current trip." : "Open GPS navigation to publish an active route here.")}
              </p>
            </div>
            <div className="absolute top-3 right-3 z-10 flex flex-col gap-2">
              {Object.entries(driverStatusConfig).map(([key, config]) => (
                <Button
                  key={`map-${key}`}
                  size="sm"
                  variant={currentStatus === key ? "default" : "outline"}
                  className="h-8"
                  onClick={() => updateDriverStatus(key)}
                  disabled={isUpdating}
                >
                  {config.label}
                </Button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* KYC Compliance Card */}
      <Card className="card-hover overflow-hidden">
        <CardHeader>
          <CardTitle className="font-heading text-xl flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-primary" />
            Driver KYC Compliance
          </CardTitle>
          <CardDescription>
            Upload transport compliance documents. You cannot accept loads until KYC is verified.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="outline" className="capitalize">
              KYC Status: {String(kycInfo.status || 'not_submitted').replaceAll('_', ' ')}
            </Badge>
            {kycInfo.status !== 'verified' && (
              <Badge className="bg-amber-500/15 text-amber-400 border border-amber-500/30">
                Load acceptance locked
              </Badge>
            )}
          </div>

          {kycInfo.status !== 'verified' && (
            <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-200 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <p>Submit required KYC documents. An admin must verify them before you can accept marketplace loads.</p>
            </div>
          )}

          <div className="space-y-2">
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>KYC Onboarding Progress</span>
              <span>{wizardProgress}%</span>
            </div>
            <Progress value={wizardProgress} />
          </div>

          <TooltipProvider>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2">
              {requiredDocSteps.map((step, index) => {
                const stepDone =
                  step.key === 'identity_info' ? true :
                  step.key === 'review_submit' ? (kycInfo.status === 'pending' || kycInfo.status === 'verified') :
                  submittedDocTypes.has(step.key);
                return (
                  <Tooltip key={step.key}>
                    <TooltipTrigger asChild>
                      <div className={`rounded-lg border p-2 text-xs ${stepDone ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300' : 'border-border bg-muted/30 text-muted-foreground'}`}>
                        <p className="font-semibold">Step {index + 1}</p>
                        <p>{step.label}</p>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>{step.tip}</TooltipContent>
                  </Tooltip>
                );
              })}
            </div>
          </TooltipProvider>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
            <div className="space-y-2">
              <Label>Document Type</Label>
              <Select value={kycDocType} onValueChange={setKycDocType}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cdl_front">CDL Front</SelectItem>
                  <SelectItem value="cdl_back">CDL Back</SelectItem>
                  <SelectItem value="selfie_verification">Selfie Verification</SelectItem>
                  <SelectItem value="medical_card">Medical Card</SelectItem>
                  <SelectItem value="insurance">Proof of Insurance</SelectItem>
                  <SelectItem value="proof_of_address">Proof of Address</SelectItem>
                  <SelectItem value="vehicle_registration">Vehicle Registration</SelectItem>
                  <SelectItem value="hazmat_endorsement">Hazmat Endorsement</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>Upload File (drag/drop, browse, or camera capture)</Label>
              <div
                className={`rounded-lg border-2 border-dashed p-4 text-center transition-colors ${dragOver ? 'border-primary bg-primary/10' : 'border-border bg-muted/20'}`}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragOver(false);
                  const file = e.dataTransfer.files?.[0];
                  if (file) setKycFile(file);
                }}
              >
                <Input
                  type="file"
                  accept="image/*,application/pdf"
                  capture={kycDocType === "selfie_verification" ? "user" : "environment"}
                  onChange={(e) => setKycFile(e.target.files?.[0] || null)}
                />
                <p className="text-xs text-muted-foreground mt-2">Tip: ensure document edges are visible and text is readable.</p>
              </div>
            </div>
          </div>
          <Button onClick={uploadKycDocument} disabled={kycUploading || !kycFile} className="w-full sm:w-auto">
            <Upload className="w-4 h-4 mr-2" />
            {kycUploading ? 'Uploading...' : 'Upload KYC Document'}
          </Button>
          {kycFile && (
            <p className="text-xs text-muted-foreground">Selected file: {kycFile.name}</p>
          )}

          {latestKyc && (
            <div className="rounded-md border border-border/60 p-3 text-sm text-muted-foreground">
              <p><span className="text-foreground font-medium">Latest submission:</span> {latestKyc.doc_type}</p>
              <p><span className="text-foreground font-medium">Status:</span> {latestKyc.status}</p>
              <p><span className="text-foreground font-medium">Uploaded:</span> {latestKyc.created_at ? new Date(latestKyc.created_at).toLocaleString() : '-'}</p>
              {latestKyc.notes && <p><span className="text-foreground font-medium">Admin note:</span> {latestKyc.notes}</p>}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Current Load */}
      {activeLoad && (
        <Card className="card-hover border-l-4 border-l-primary">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading text-xl">Current Load</CardTitle>
              <span className={`px-2 py-1 rounded text-xs font-bold uppercase border ${statusColors[activeLoad.status]}`}>
                {activeLoad.status.replace('_', ' ')}
              </span>
            </div>
            <CardDescription className="font-mono">{activeLoad.tracking_number}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Origin</p>
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-primary" />
                  <p className="font-medium">{activeLoad.origin}</p>
                </div>
              </div>
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Destination</p>
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-emerald-400" />
                  <p className="font-medium">{activeLoad.destination}</p>
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-3 gap-4 pt-4 border-t border-border">
              <div>
                <p className="text-xs text-muted-foreground">Cargo</p>
                <p className="font-medium">{activeLoad.cargo_type}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Weight</p>
                <p className="font-medium">{activeLoad.weight} lbs</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Delivery</p>
                <p className="font-medium">{activeLoad.delivery_date}</p>
              </div>
            </div>

            {activeLoad.special_instructions && (
              <div className="p-3 bg-muted rounded-lg">
                <p className="text-xs text-muted-foreground uppercase mb-1">Special Instructions</p>
                <p className="text-sm">{activeLoad.special_instructions}</p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3 pt-4">
              {activeLoad.status === 'assigned' && (
                <Button
                  className="bg-primary hover:bg-primary/90 shadow-glow"
                  onClick={() => updateShipmentStatus(activeLoad.id, 'in_transit')}
                  data-testid="start-delivery-btn"
                >
                  <Truck className="w-4 h-4 mr-2" />
                  Start Delivery
                </Button>
              )}
              {activeLoad.status === 'in_transit' && (
                <Button
                  className="bg-emerald-600 hover:bg-emerald-700"
                  onClick={() => updateShipmentStatus(activeLoad.id, 'delivered')}
                  data-testid="complete-delivery-btn"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Complete Delivery
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* No Active Load */}
      {!activeLoad && !isLoading && (
        <Card className="card-hover">
          <CardContent className="py-12 text-center">
            <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
              <Package className="w-8 h-8 text-muted-foreground" />
            </div>
            <h3 className="font-heading text-xl mb-2">No Active Loads</h3>
            <p className="text-muted-foreground">You don't have any assigned loads right now.</p>
          </CardContent>
        </Card>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4">
        <Card className="card-hover">
          <CardContent className="p-6 text-center">
            <Clock className="w-8 h-8 text-amber-400 mx-auto mb-2" />
            <p className="text-3xl font-heading font-bold">{pendingLoads.length}</p>
            <p className="text-sm text-muted-foreground">Pending Loads</p>
          </CardContent>
        </Card>
        <Card className="card-hover">
          <CardContent className="p-6 text-center">
            <CheckCircle className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
            <p className="text-3xl font-heading font-bold">{completedLoads.length}</p>
            <p className="text-sm text-muted-foreground">Completed</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

const LoadsPage = () => {
  const [shipments, setShipments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchShipments = async () => {
      try {
        const response = await axios.get(`${API}/shipments`);
        setShipments(response.data);
      } catch (error) {
        toast.error('Failed to fetch loads');
      } finally {
        setIsLoading(false);
      }
    };
    fetchShipments();
  }, []);

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <h1 className="text-3xl font-heading font-bold tracking-tight">My Loads</h1>
      
      {shipments.length === 0 && !isLoading ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Package className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No loads assigned yet</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {shipments.map((shipment) => (
            <Card key={shipment.id} className="card-hover">
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="font-mono text-sm text-muted-foreground">{shipment.tracking_number}</p>
                    <p className="font-heading font-semibold">{shipment.cargo_type}</p>
                  </div>
                  <span className={`px-2 py-1 rounded text-xs font-bold uppercase border ${statusColors[shipment.status]}`}>
                    {shipment.status.replace('_', ' ')}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <MapPin className="w-4 h-4" />
                  <span>{shipment.origin}</span>
                  <span>→</span>
                  <span>{shipment.destination}</span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

const DriverDashboard = () => {
  return (
    <div className="min-h-screen bg-background">
      <Sidebar role="driver" />
      <main className="lg:ml-64 p-4 lg:p-8" data-testid="driver-dashboard">
        <div className="flex justify-end mb-4">
          <DashboardDateTimeWidget />
        </div>
        <Routes>
          <Route index element={<DriverHome />} />
          <Route path="loads" element={<LoadsPage />} />
        </Routes>
      </main>
    </div>
  );
};

export default DriverDashboard;
